import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type {
  RouteDebugInput,
  RouteDebugOutput,
  DeviceContext,
  TargetResponseInfo,
  FinalResponseInfo,
} from '../types';
import { debugRoute } from '../api/http-route-api';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'] as const;

/** Collapsible section state map */
interface SectionState {
  device_context: boolean;
  matched_rule: boolean;
  target_system: boolean;
  target_url: boolean;
  forward_request_preview: boolean;
  target_response: boolean;
  final_response: boolean;
}

function defaultSectionState(): SectionState {
  return {
    device_context: true,
    matched_rule: true,
    target_system: true,
    target_url: true,
    forward_request_preview: true,
    target_response: true,
    final_response: true,
  };
}

@customElement('http-route-debug-view')
export class HttpRouteDebugView extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    }

    .page-wrap {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
      gap: 1.5rem;
      align-items: start;
    }

    @media (max-width: 900px) {
      .page-wrap {
        grid-template-columns: 1fr;
      }
    }

    .panel {
      background: white;
      border-radius: 8px;
      padding: 1.25rem 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
      border: 1px solid #e1e4e8;
    }

    .panel h2 {
      margin: 0 0 1rem;
      font-size: 1rem;
      font-weight: 600;
      color: #102a43;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e1e4e8;
    }

    .field {
      margin-bottom: 1rem;
    }

    .field:last-of-type {
      margin-bottom: 0;
    }

    .field-row {
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .field-row .field-grow {
      flex: 1 1 180px;
      min-width: 0;
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #334e68;
    }

    input[type='text'],
    select,
    textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bcccdc;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.2s;
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: #2684ff;
    }

    textarea {
      min-height: 80px;
      resize: vertical;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.8125rem;
      line-height: 1.45;
    }

    textarea.headers-area {
      min-height: 60px;
    }

    .actions {
      margin: 1rem 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-debug {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      transition: background-color 0.2s;
      background-color: #2684ff;
      color: white;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }

    .btn-debug:hover:not(:disabled) {
      background-color: #005cc5;
    }

    .btn-debug:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .btn-reset {
      border: 1px solid #bcccdc;
      background: white;
      color: #334e68;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      transition: border-color 0.2s, color 0.2s;
    }

    .btn-reset:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .btn-reset:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .field-hint {
      font-size: 0.75rem;
      color: #627d98;
      margin-top: 0.35rem;
      line-height: 1.45;
    }

    .field-hint code {
      font-size: 0.7rem;
      padding: 0.1rem 0.35rem;
      border-radius: 4px;
      background: #f0f4f8;
      color: #334e68;
    }

    .output-section {
      margin-bottom: 0.75rem;
    }

    /* Collapsible section */
    .section-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      background: #f8fafc;
      border: 1px solid #e1e4e8;
      transition: background 0.15s;
      user-select: none;
    }

    .section-header:hover {
      background: #f0f4f8;
    }

    .section-header .arrow {
      font-size: 0.7rem;
      color: #627d98;
      transition: transform 0.2s;
      display: inline-block;
      width: 1rem;
      text-align: center;
    }

    .section-header .arrow.open {
      transform: rotate(90deg);
    }

    .section-label {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #334e68;
      flex: 1;
    }

    .section-badge {
      font-size: 0.6875rem;
      font-weight: 600;
      padding: 0.15rem 0.4rem;
      border-radius: 4px;
      text-transform: uppercase;
    }

    .section-badge.ok {
      background: #d1fae5;
      color: #065f46;
    }

    .section-badge.null {
      background: #fef3c7;
      color: #92400e;
    }

    .section-body {
      padding: 0.75rem;
      border: 1px solid #e1e4e8;
      border-top: none;
      border-radius: 0 0 6px 6px;
      background: #fbfcfd;
    }

    .section-body pre {
      margin: 0;
      padding: 0.75rem;
      background: #0f172a;
      color: #e2e8f0;
      border-radius: 6px;
      font-size: 0.75rem;
      line-height: 1.5;
      overflow: auto;
      max-height: 300px;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .section-body pre.error {
      background: #450a0a;
      color: #fecaca;
    }

    .section-body .null-marker {
      font-size: 0.8125rem;
      color: #627d98;
      font-style: italic;
      padding: 0.5rem;
    }

    .error-banner {
      background: #fee2e2;
      border: 1px solid #fecaca;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      margin-bottom: 1rem;
      color: #991b1b;
      font-size: 0.875rem;
    }

    .kv-list {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.35rem 0.75rem;
      font-size: 0.8125rem;
      padding: 0.5rem;
    }

    .kv-key {
      font-weight: 600;
      color: #486581;
      white-space: nowrap;
    }

    .kv-val {
      color: #102a43;
      word-break: break-all;
    }

    .kv-val code {
      background: #f0f4f8;
      padding: 0.1rem 0.35rem;
      border-radius: 3px;
      font-size: 0.75rem;
      color: #334e68;
    }

    .warn-banner {
      background: #fffbeb;
      border: 1px solid #fde68a;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      margin-bottom: 1rem;
      color: #92400e;
      font-size: 0.8125rem;
      line-height: 1.5;
    }

    .http-meta {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.35rem 0.75rem;
      font-size: 0.8125rem;
      padding: 0 0 0.75rem;
      margin-bottom: 0.75rem;
      border-bottom: 1px dashed #e2e8f0;
    }

    .raw-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #486581;
      margin-bottom: 0.35rem;
    }
  `;

  // ── Input state ──
  @state() private sn = '';
  @state() private method: string = 'GET';
  @state() private path = '';
  @state() private query = '';
  @state() private body = '';
  @state() private loading = false;

  // ── Output state ──
  @state() private result: RouteDebugOutput | null = null;
  @state() private errorMessage = '';
  @state() private sections: SectionState = defaultSectionState();

  private toggleSection(section: keyof SectionState) {
    this.sections = { ...this.sections, [section]: !this.sections[section] };
  }

  private formatRawBody(body: string): string {
    const trimmed = body.trim();
    if (!trimmed) return '（空响应体）';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return body;
    }
  }

  private renderHttpRawResponse(
    label: string,
    info: TargetResponseInfo | FinalResponseInfo | null,
    section: keyof SectionState
  ) {
    const isOpen = this.sections[section];
    const hasData = info !== null && info !== undefined;
    const isError = hasData && info!.status_code >= 400;
    return html`
      <div class="output-section">
        <div class="section-header" @click=${() => this.toggleSection(section)}>
          <span class="arrow ${isOpen ? 'open' : ''}">&#9654;</span>
          <span class="section-label">${label}</span>
          <span class="section-badge ${hasData ? 'ok' : 'null'}">
            ${hasData ? `HTTP ${info!.status_code}` : 'NULL'}
          </span>
        </div>
        ${isOpen
          ? html`<div class="section-body">
              ${hasData
                ? html`
                    <div class="http-meta">
                      <span class="kv-key">Status</span>
                      <span class="kv-val">${info!.status_code}</span>
                      <span class="kv-key">Content-Type</span>
                      <span class="kv-val">${info!.content_type || '—'}</span>
                      ${'body_length' in info && info.body_length !== undefined
                        ? html`<span class="kv-key">Body 长度</span><span class="kv-val">${info.body_length} bytes</span>`
                        : null}
                      ${'response_mode' in info && info.response_mode
                        ? html`<span class="kv-key">响应模式</span><span class="kv-val">${info.response_mode}</span>`
                        : null}
                      ${'headers' in info && info.headers && Object.keys(info.headers).length > 0
                        ? html`<span class="kv-key">Response Headers</span>
                            <span class="kv-val"
                              ><pre>${JSON.stringify(info.headers, null, 2)}</pre></span
                            >`
                        : null}
                    </div>
                    <div class="raw-label">原始响应体 (Raw Body)</div>
                    <pre class=${isError ? 'error' : ''}>${this.formatRawBody(info!.body)}</pre>
                  `
                : html`<div class="null-marker">（无数据 — 请先匹配路由并成功转发）</div>`}
            </div>`
          : ''}
      </div>
    `;
  }

  private async handleDebug() {
    const snVal = this.sn.trim();
    const pathVal = this.path.trim();

    if (!snVal) {
      this.errorMessage = '请输入设备 SN';
      return;
    }
    if (!pathVal) {
      this.errorMessage = '请输入请求路径';
      return;
    }

    const input: RouteDebugInput = {
      sn: snVal,
      method: this.method,
      path: pathVal,
      query: this.query.trim() || undefined,
      body: this.body.trim() || undefined,
    };

    console.log('[http-route-debug] input=', input);
    this.errorMessage = '';
    this.loading = true;

    try {
      const output = await debugRoute(input);
      console.log('[http-route-debug] target raw response=', output.target_response);
      console.log('[http-route-debug] final response=', output.final_response);
      this.result = output;
      this.sections = defaultSectionState();
    } catch (err) {
      this.result = null;
      this.errorMessage = err instanceof Error ? err.message : '调试请求失败';
    } finally {
      this.loading = false;
    }
  }

  private handleReset() {
    this.sn = '';
    this.method = 'GET';
    this.path = '';
    this.query = '';
    this.body = '';
    this.result = null;
    this.errorMessage = '';
  }

  // ── Render helpers ──

  private renderSectionHeader(
    label: string,
    section: keyof SectionState,
    data: unknown | null
  ) {
    const isOpen = this.sections[section];
    const hasData = data !== null && data !== undefined;
    return html`
      <div class="section-header" @click=${() => this.toggleSection(section)}>
        <span class="arrow ${isOpen ? 'open' : ''}">&#9654;</span>
        <span class="section-label">${label}</span>
        <span class="section-badge ${hasData ? 'ok' : 'null'}">
          ${hasData ? 'OK' : 'NULL'}
        </span>
      </div>
      ${isOpen ? this.renderSectionBody(data) : ''}
    `;
  }

  private renderSectionBody(data: unknown | null) {
    if (data === null || data === undefined) {
      return html`<div class="section-body"><div class="null-marker">（无数据）</div></div>`;
    }
    return html`
      <div class="section-body">
        <pre>${JSON.stringify(data, null, 2)}</pre>
      </div>
    `;
  }

  private renderDeviceContext(ctx: DeviceContext | null) {
    const isOpen = this.sections.device_context;
    const hasData = ctx !== null && ctx !== undefined;
    return html`
      <div class="output-section">
        <div class="section-header" @click=${() => this.toggleSection('device_context')}>
          <span class="arrow ${isOpen ? 'open' : ''}">&#9654;</span>
          <span class="section-label">1. 设备上下文 (Device Context)</span>
          <span class="section-badge ${hasData ? 'ok' : 'null'}">
            ${hasData ? 'OK' : 'NULL'}
          </span>
        </div>
        ${isOpen
          ? html`<div class="section-body">
              ${hasData
                ? html`<div class="kv-list">
                    <span class="kv-key">SN:</span>
                    <span class="kv-val">${ctx!.sn}</span>
                    <span class="kv-key">租户ID:</span>
                    <span class="kv-val">${ctx!.tenant_id || '—'}</span>
                    <span class="kv-key">项目ID:</span>
                    <span class="kv-val">${ctx!.project_id || '—'}</span>
                    <span class="kv-key">绑定系统:</span>
                    <span class="kv-val">${ctx!.bind_systems?.length ? ctx!.bind_systems.join(', ') : '—'}</span>
                  </div>`
                : html`<div class="null-marker">（无数据）</div>`}
              ${this.result?.device_context_error
                ? html`<div class="warn-banner" style="margin-top:0.75rem">
                    设备上下文获取失败（不影响路由匹配）：${this.result.device_context_error}
                  </div>`
                : null}
            </div>`
          : ''}
      </div>
    `;
  }

  render() {
    const hasResult = this.result !== null;

    return html`
      <div class="page-wrap">
        <!-- Input panel -->
        <section class="panel" aria-labelledby="input-title">
          <h2 id="input-title">路由调试输入</h2>

          <div class="field">
            <label for="debug-sn">设备 SN</label>
            <input
              id="debug-sn"
              type="text"
              .value=${this.sn}
              placeholder="例如 CAR-20240001"
              @input=${(e: Event) => {
                this.sn = (e.target as HTMLInputElement).value;
              }}
            />
          </div>

          <div class="field">
            <label for="debug-method">请求方法</label>
            <select
              id="debug-method"
              .value=${this.method}
              @change=${(e: Event) => {
                this.method = (e.target as HTMLSelectElement).value;
              }}
            >
              ${HTTP_METHODS.map(
                (m) => html`<option value=${m}>${m}</option>`
              )}
            </select>
          </div>

          <div class="field">
            <label for="debug-path">请求路径</label>
            <input
              id="debug-path"
              type="text"
              .value=${this.path}
              placeholder="例如 /api/car-api/patrol-plan/info"
              @input=${(e: Event) => {
                this.path = (e.target as HTMLInputElement).value;
              }}
            />
            <p class="field-hint">
              填<strong>网关源路径</strong>（与路由规则表 source_path 一致），如
              <code>/api/car-api/patrol-plan/info</code>。
              南向鉴权头 <code>sn</code> / <code>timestamp</code> / <code>sign</code> 由服务端按当前 SN 与参数自动生成，无需手填。
            </p>
          </div>

          <div class="field">
            <label for="debug-query">Query 参数（可选）</label>
            <input
              id="debug-query"
              type="text"
              .value=${this.query}
              placeholder="例如 sn=CAR001&type=status"
              @input=${(e: Event) => {
                this.query = (e.target as HTMLInputElement).value;
              }}
            />
          </div>

          <div class="field">
            <label for="debug-body">请求 Body（可选，JSON 格式）</label>
            <textarea
              id="debug-body"
              .value=${this.body}
              placeholder='例如 {"key": "value"}'
              @input=${(e: Event) => {
                this.body = (e.target as HTMLTextAreaElement).value;
              }}
              spellcheck="false"
            ></textarea>
          </div>

          <div class="actions">
            <button
              type="button"
              class="btn-debug"
              ?disabled=${this.loading}
              @click=${() => this.handleDebug()}
            >
              ${this.loading ? '调试中…' : '开始调试'}
            </button>
            <button
              type="button"
              class="btn-reset"
              ?disabled=${this.loading}
              @click=${() => this.handleReset()}
            >
              重置
            </button>
          </div>

          ${this.errorMessage
            ? html`<div class="error-banner">${this.errorMessage}</div>`
            : ''}
        </section>

        <!-- Output panel -->
        <section class="panel" aria-labelledby="output-title">
          <h2 id="output-title">调试输出</h2>

          ${!hasResult && !this.errorMessage
            ? html`<p class="field-hint">填写左侧调试参数后点击「开始调试」查看 7 段路由诊断结果。</p>`
            : ''}

          ${hasResult
            ? html`
                ${this.result!.match_error
                  ? html`<div class="warn-banner">
                      <strong>路由未匹配：</strong>${this.result!.match_error}<br />
                      请使用源路径，例如 <code>/car-api/patrol-plan/info</code>，不要填目标平台的
                      <code>/api/patrol/info</code>。
                    </div>`
                  : null}

                ${this.renderDeviceContext(this.result!.device_context)}

                <div class="output-section">
                  ${this.renderSectionHeader('2. 匹配规则 (Matched Rule)', 'matched_rule', this.result!.matched_rule)}
                </div>

                <div class="output-section">
                  ${this.renderSectionHeader('3. 目标系统 (Target System)', 'target_system', this.result!.target_system)}
                </div>

                <div class="output-section">
                  <div class="section-header" @click=${() => this.toggleSection('target_url')}>
                    <span class="arrow ${this.sections.target_url ? 'open' : ''}">&#9654;</span>
                    <span class="section-label">4. 目标 URL (Target URL)</span>
                    <span class="section-badge ${this.result!.target_url ? 'ok' : 'null'}">
                      ${this.result!.target_url ? 'OK' : 'NULL'}
                    </span>
                  </div>
                  ${this.sections.target_url
                    ? html`<div class="section-body">
                        ${this.result!.target_url
                          ? html`<pre>${this.result!.target_url}</pre>`
                          : html`<div class="null-marker">（无数据）</div>`}
                      </div>`
                    : ''}
                </div>

                <div class="output-section">
                  ${this.renderSectionHeader('5. 转发请求预览 (Forward Request Preview)', 'forward_request_preview', this.result!.forward_request_preview)}
                </div>

                ${this.renderHttpRawResponse(
                  '6. 目标平台原始响应 (Upstream Raw Response)',
                  this.result!.target_response,
                  'target_response'
                )}

                ${this.renderHttpRawResponse(
                  '7. 网关最终响应 (Gateway Final Response)',
                  this.result!.final_response,
                  'final_response'
                )}
              `
            : ''}
        </section>
      </div>
    `;
  }
}
